import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import loyaltyService from '../../../services/loyaltyService';

const RewardsCatalog = ({ userBalance, onRedemption }) => {
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedReward, setSelectedReward] = useState(null);
  const [redeeming, setRedeeming] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Load rewards catalog
  useEffect(() => {
    const loadRewards = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const { data, error } = await loyaltyService?.getRewardsCatalog(selectedCategory);
        
        if (error) {
          setError(error?.message || 'Failed to load rewards');
        } else {
          setRewards(data || []);
        }
      } catch (err) {
        setError('Failed to connect to rewards service');
      } finally {
        setLoading(false);
      }
    };

    loadRewards();
  }, [selectedCategory]);

  // Filter rewards based on search
  const filteredRewards = rewards?.filter(reward =>
    reward?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    reward?.description?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  );

  // Handle reward redemption
  const handleRedeem = async (reward, quantity = 1) => {
    if (!reward || redeeming) return;
    
    const totalCost = reward?.token_cost * quantity;
    
    if (userBalance < totalCost) {
      alert('Insufficient tokens for this redemption.');
      return;
    }
    
    setRedeeming(true);
    
    try {
      const { data, error } = await loyaltyService?.redeemReward(
        reward?.user_id || 'current-user', // This should come from auth context
        reward?.id,
        quantity
      );
      
      if (error) {
        alert(error?.message || 'Failed to redeem reward');
      } else {
        alert(`Successfully redeemed ${reward?.title}!`);
        setSelectedReward(null);
        onRedemption?.(); // Refresh wallet balance
        
        // Update local rewards list to reflect stock changes
        setRewards(prevRewards => 
          prevRewards?.map(r => 
            r?.id === reward?.id 
              ? { ...r, stock_quantity: r?.stock_quantity ? r?.stock_quantity - quantity : r?.stock_quantity }
              : r
          )
        );
      }
    } catch (err) {
      alert('Failed to process redemption. Please try again.');
    } finally {
      setRedeeming(false);
    }
  };

  // Get category icon
  const getCategoryIcon = (category) => {
    const icons = {
      'wellness_products': 'Heart',
      'marketplace_discount': 'Percent',
      'workshop_access': 'BookOpen',
      'consultation_time': 'Users',
      'premium_content': 'Star',
      'merchandise': 'ShoppingBag',
      'gift_card': 'Gift'
    };
    return icons?.[category] || 'Gift';
  };

  // Get category color
  const getCategoryColor = (category) => {
    const colors = {
      'wellness_products': 'bg-green-100 text-green-800',
      'marketplace_discount': 'bg-blue-100 text-blue-800',
      'workshop_access': 'bg-purple-100 text-purple-800',
      'consultation_time': 'bg-orange-100 text-orange-800',
      'premium_content': 'bg-yellow-100 text-yellow-800',
      'merchandise': 'bg-pink-100 text-pink-800',
      'gift_card': 'bg-indigo-100 text-indigo-800'
    };
    return colors?.[category] || 'bg-gray-100 text-gray-800';
  };

  const categories = loyaltyService?.getRewardCategories();

  return (
    <div className="space-y-6">
      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Icon name="Search" size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search rewards..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            className="w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-card text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
          />
        </div>
        
        <div className="flex items-center space-x-2">
          <span className="text-sm font-medium text-foreground">Category:</span>
          <select
            value={selectedCategory || ''}
            onChange={(e) => setSelectedCategory(e?.target?.value || null)}
            className="px-3 py-2 border border-border rounded-lg bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
          >
            <option value="">All Categories</option>
            {categories?.map((category) => (
              <option key={category?.value} value={category?.value}>
                {category?.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      {/* Balance Display */}
      <div className="bg-primary/10 rounded-lg p-4 border border-primary/20">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
              <Icon name="Coins" size={20} color="white" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Available Balance</p>
              <p className="text-xl font-bold text-foreground">
                {loyaltyService?.formatTokens(userBalance)} tokens
              </p>
            </div>
          </div>
          <Icon name="Wallet" size={24} className="text-primary" />
        </div>
      </div>
      {/* Error State */}
      {error && (
        <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-center space-x-3">
          <Icon name="AlertTriangle" size={20} className="text-destructive" />
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}
      {/* Loading State */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)]?.map((_, i) => (
            <div key={i} className="bg-card rounded-lg border border-border p-6 animate-pulse">
              <div className="w-full h-32 bg-muted rounded-lg mb-4"></div>
              <div className="space-y-3">
                <div className="h-4 bg-muted rounded w-3/4"></div>
                <div className="h-3 bg-muted rounded w-full"></div>
                <div className="h-3 bg-muted rounded w-1/2"></div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* Rewards Grid */
        (<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredRewards?.length === 0 ? (
            <div className="col-span-full text-center py-12">
              <Icon name="Gift" size={48} className="mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-semibold text-foreground mb-2">No Rewards Found</h3>
              <p className="text-muted-foreground">
                {searchTerm ? 'Try adjusting your search terms' : 'No rewards available in this category'}
              </p>
            </div>
          ) : (
            filteredRewards?.map((reward) => {
              const canAfford = userBalance >= reward?.token_cost;
              const isOutOfStock = reward?.stock_quantity === 0;
              const isUnavailable = !canAfford || isOutOfStock;

              return (
                <div
                  key={reward?.id}
                  className={`bg-card rounded-lg border shadow-wellness transition-all duration-200 hover:shadow-lg ${
                    isUnavailable ? 'opacity-60' : 'cursor-pointer hover:border-primary/30'
                  }`}
                  onClick={() => !isUnavailable && setSelectedReward(reward)}
                >
                  {/* Reward Image */}
                  <div className="aspect-video bg-muted rounded-t-lg flex items-center justify-center relative overflow-hidden">
                    {reward?.image_url ? (
                      <img
                        src={reward?.image_url}
                        alt={reward?.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Icon
                        name={getCategoryIcon(reward?.category)}
                        size={48}
                        className="text-muted-foreground"
                      />
                    )}
                    
                    {/* Category Badge */}
                    <div className={`absolute top-2 left-2 px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(reward?.category)}`}>
                      {categories?.find(c => c?.value === reward?.category)?.label || reward?.category}
                    </div>
                    
                    {/* Stock Status */}
                    {isOutOfStock && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                        <span className="text-white font-medium">Out of Stock</span>
                      </div>
                    )}
                  </div>
                  {/* Reward Details */}
                  <div className="p-4">
                    <h3 className="font-semibold text-foreground mb-2 line-clamp-1">
                      {reward?.title}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                      {reward?.description}
                    </p>

                    {/* Token Cost and Stock Info */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <Icon name="Coins" size={16} className="text-primary" />
                        <span className="font-bold text-primary">
                          {loyaltyService?.formatTokens(reward?.token_cost)}
                        </span>
                        <span className="text-sm text-muted-foreground">tokens</span>
                      </div>
                      
                      {reward?.stock_quantity !== null && (
                        <span className="text-xs text-muted-foreground">
                          {reward?.stock_quantity} left
                        </span>
                      )}
                    </div>

                    {/* Action Button */}
                    <button
                      onClick={(e) => {
                        e?.stopPropagation();
                        if (!isUnavailable) setSelectedReward(reward);
                      }}
                      disabled={isUnavailable}
                      className={`w-full py-2 px-4 rounded-lg font-medium transition-colors ${
                        isUnavailable
                          ? 'bg-muted text-muted-foreground cursor-not-allowed'
                          : 'bg-primary text-primary-foreground hover:bg-primary/90'
                      }`}
                    >
                      {isOutOfStock ? 'Out of Stock' : !canAfford ? 'Insufficient Tokens' : 'Redeem'}
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>)
      )}
      {/* Redemption Modal */}
      {selectedReward && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg border border-border max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-foreground">Confirm Redemption</h3>
                <button
                  onClick={() => setSelectedReward(null)}
                  className="p-1 hover:bg-muted rounded-lg transition-colors"
                >
                  <Icon name="X" size={20} className="text-muted-foreground" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-foreground mb-2">{selectedReward?.title}</h4>
                  <p className="text-sm text-muted-foreground mb-3">{selectedReward?.description}</p>
                  
                  <div className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(selectedReward?.category)}`}>
                    {categories?.find(c => c?.value === selectedReward?.category)?.label}
                  </div>
                </div>

                <div className="flex items-center justify-between py-3 border-t border-b border-border">
                  <span className="font-medium text-foreground">Token Cost:</span>
                  <div className="flex items-center space-x-2">
                    <Icon name="Coins" size={16} className="text-primary" />
                    <span className="font-bold text-primary">
                      {loyaltyService?.formatTokens(selectedReward?.token_cost)}
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Your Balance:</span>
                  <span className="text-sm font-medium text-foreground">
                    {loyaltyService?.formatTokens(userBalance)} tokens
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">After Redemption:</span>
                  <span className="text-sm font-medium text-foreground">
                    {loyaltyService?.formatTokens(userBalance - selectedReward?.token_cost)} tokens
                  </span>
                </div>

                {selectedReward?.terms_conditions && (
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <p className="text-xs text-muted-foreground">
                      <span className="font-medium">Terms & Conditions:</span> {selectedReward?.terms_conditions}
                    </p>
                  </div>
                )}
              </div>

              <div className="flex space-x-3 mt-6">
                <button
                  onClick={() => setSelectedReward(null)}
                  className="flex-1 py-2 px-4 border border-border rounded-lg text-foreground hover:bg-muted/50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleRedeem(selectedReward)}
                  disabled={redeeming || userBalance < selectedReward?.token_cost}
                  className="flex-1 py-2 px-4 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {redeeming ? 'Redeeming...' : 'Confirm Redemption'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RewardsCatalog;